import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Leaf, Mail, Lock, User, ArrowRight, Globe, Eye, EyeOff, AlertCircle, LogOut } from 'lucide-react';
import axios from 'axios';
import PasswordStrength from '../components/PasswordStrength';
import { API_URL } from '../utils/apiConfig';

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const { user, isAuthenticated, logout, setUser } = useAuth();
  const { isDark } = useTheme();
  const { t } = useLanguage();

  const [invitationData, setInvitationData] = useState(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [invitLang, setInvitLang] = useState('fr');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState(null); // 'invalid' | 'expired'

  useEffect(() => {
    if (!token) {
      setErrorType('invalid');
      setLoading(false);
      return;
    }
    if (isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorType(null);

    axios.get(`${API_URL}/api/auth/invitation/${token}`)
      .then(res => {
        setInvitationData(res.data);
        setInvitLang(res.data.language || 'fr');
      })
      .catch(err => {
        setErrorType(err.response?.status === 410 ? 'expired' : 'invalid');
      })
      .finally(() => setLoading(false));
  }, [token, isAuthenticated]);

  const handleLogoutAndContinue = async () => {
    await logout();
    // isAuthenticated becomes false → effect re-runs → fetches invitation
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/accept-invitation`, {
        token,
        name,
        password,
        language: invitLang,
      });
      setUser(res.data.user);
      navigate('/');
    } catch (err) {
      if (err.response?.status === 410) {
        setErrorType('expired');
      } else {
        setError(err.response?.data?.detail || t('acceptInvitation.errorUnknown'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const card = `p-8 rounded-2xl ${isDark ? 'bg-slate-800' : 'bg-white shadow-xl shadow-gray-200/50'}`;
  const bg = `min-h-screen flex items-center justify-center p-8 ${isDark ? 'bg-slate-900' : 'bg-gradient-to-br from-blue-50 via-white to-green-50'}`;
  const inputCls = `w-full pl-12 pr-4 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`;
  const labelCls = `block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`;
  const iconCls = `absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-400' : 'text-gray-400'}`;

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={bg}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  // ── Already connected ────────────────────────────────────────────────────
  if (isAuthenticated) {
    return (
      <div className={bg}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className={card}>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mx-auto mb-4">
                <Leaf className="w-10 h-10 text-white" />
              </div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('acceptInvitation.alreadyConnectedTitle')}
              </h2>
              <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {t('acceptInvitation.alreadyConnectedDesc')}
              </p>
            </div>
            <div className={`mb-6 p-4 rounded-xl text-sm ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-gray-50 text-gray-600'}`}>
              <span className="font-medium">{t('acceptInvitation.alreadyConnectedEmail')} </span>
              {user?.email}
            </div>
            <button
              onClick={handleLogoutAndContinue}
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/30"
            >
              <LogOut className="w-5 h-5" />
              {t('acceptInvitation.alreadyConnectedAction')}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Error states ─────────────────────────────────────────────────────────
  if (errorType) {
    const isExpired = errorType === 'expired';
    return (
      <div className={bg}>
        <div className={`${card} text-center max-w-md w-full`}>
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {isExpired ? t('acceptInvitation.errorExpiredTitle') : t('acceptInvitation.errorInvalidTitle')}
          </h2>
          <p className={`mb-6 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            {isExpired ? t('acceptInvitation.errorExpiredDesc') : t('acceptInvitation.errorInvalidDesc')}
          </p>
          <Link to="/auth" className="text-blue-500 font-medium hover:underline">
            {t('acceptInvitation.backToLogin')}
          </Link>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  const roleLabel = invitationData?.role === 'admin'
    ? t('acceptInvitation.roleAdmin')
    : t('acceptInvitation.roleMember');

  return (
    <div className={bg}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className={card}>
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center mx-auto mb-4">
              <Leaf className="w-10 h-10 text-white" />
            </div>
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('acceptInvitation.title')}
            </h2>
            <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('acceptInvitation.subtitle').replace('{company}', invitationData?.company_name || '')}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email — readonly */}
            <div>
              <label className={labelCls}>{t('acceptInvitation.emailLabel')}</label>
              <div className="relative">
                <Mail className={iconCls} />
                <input
                  type="email"
                  value={invitationData?.email || ''}
                  readOnly
                  className={`w-full pl-12 pr-4 py-3 rounded-xl border cursor-default ${isDark ? 'bg-slate-700/50 border-slate-600 text-slate-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
                />
              </div>
            </div>

            {/* Name */}
            <div>
              <label className={labelCls}>{t('acceptInvitation.nameLabel')}</label>
              <div className="relative">
                <User className={iconCls} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="accept-name-input"
                  className={inputCls}
                  placeholder={t('acceptInvitation.nameLabel')}
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className={labelCls}>{t('acceptInvitation.passwordLabel')}</label>
              <div className="relative">
                <Lock className={iconCls} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="accept-password-input"
                  className={`w-full pl-12 pr-12 py-3 rounded-xl border transition-all focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <PasswordStrength password={password} />
            </div>

            {/* Language */}
            <div>
              <label className={labelCls}>{t('acceptInvitation.languageLabel')}</label>
              <div className="relative">
                <Globe className={iconCls} />
                <select
                  value={invitLang}
                  onChange={(e) => setInvitLang(e.target.value)}
                  data-testid="accept-language-select"
                  className={`${inputCls} appearance-none`}
                >
                  <option value="fr">{t('acceptInvitation.langFr')}</option>
                  <option value="de">{t('acceptInvitation.langDe')}</option>
                </select>
              </div>
            </div>

            {/* Company info */}
            <div className={`p-4 rounded-xl text-sm ${isDark ? 'bg-slate-700/50 text-slate-300' : 'bg-blue-50 text-gray-600'}`}>
              {t('acceptInvitation.infoText')
                .replace('{company}', invitationData?.company_name || '')
                .replace('{role}', roleLabel)
                .replace('{inviter}', invitationData?.inviter_name || '')}
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="accept-submit-btn"
              className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 shadow-lg shadow-blue-500/30"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {t('acceptInvitation.submitButton')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <p className={`mt-6 text-center text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            <Link to="/auth" className="text-blue-500 font-medium hover:underline">
              {t('acceptInvitation.backToLogin')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AcceptInvitation;
